import type { FC } from "react";

import type { OrgChannelSummary } from "./useOrgChannels";

import { Button } from "../common/Button";
import { HudStatusPill, type HudStatusTone } from "../common/HudStatusPill";
import { formatRelativeTime } from "./environment/environmentUtils";

/** Provenance origin of a lifecycle channel, rendered as a small chip in the
 *  lifecycle ledger so an operator can tell an issue-workspace room from a
 *  hand-made shared one at a glance. */
export function provenanceLabel(channel: OrgChannelSummary): string {
  switch (channel.provenance) {
    case "issue_workspace": {
      return "issue · ws";
    }
    case "teams_bind": {
      return "teams · bind";
    }
    default: {
      return channel.kind === "private" ? "private" : "manual · shared";
    }
  }
}

/**
 * Channels the lifecycle ledger owns. A channel that lost its last member is
 * included from the moment `memberlessSince` is stamped — NOT only once the
 * dormancy sweeper archives it, which at a 168h grace is seven days later.
 * Before this, such a channel was reachable from nowhere in the UI: the fleet
 * grid is built from instances, so a zero-member channel cannot form a group.
 */
export function selectLifecycleChannels(
  channels: readonly OrgChannelSummary[],
): OrgChannelSummary[] {
  return channels.filter(
    (channel) =>
      channel.archivedAt != null ||
      channel.deletedAt != null ||
      channel.memberlessSince != null,
  );
}

/**
 * Maps a lifecycle channel to its status pill. Three distinct resting states:
 * soft-deleted rooms read as settled "retained" (violet); archived rooms as
 * "dormant" (amber); a memberless room still inside its pre-archive grace
 * window as "memberless · grace period" (sky) — deliberately distinct so an
 * operator can tell "the sweeper already claimed this" from "the sweeper will
 * claim this", and so retained isn't mistaken for a warning.
 */
export function lifecycleStatus(channel: OrgChannelSummary): {
  tone: HudStatusTone;
  label: string;
} {
  if (channel.deletedAt != null) {
    return {
      label: `deleted · ${channel.deletedReason ?? "retained"}`,
      tone: "retained",
    };
  }
  if (channel.archivedAt != null) {
    const reason = (channel.archivedReason ?? "archived").replace(
      "dormant_",
      "",
    );
    return { label: `dormant · ${reason}`, tone: "warning" };
  }
  return { label: "memberless · grace period", tone: "info" };
}

/** Whether an operator can put an actor back into this room. Archived-dormant
 *  rooms are restorable, and so is a memberless room still in its grace
 *  window — adding a member there clears the marker before the sweeper runs. */
export function canAddActorToLifecycleChannel(
  channel: OrgChannelSummary,
): boolean {
  if (channel.deletedAt != null || channel.provenance !== "manual") {
    return false;
  }
  if (channel.archivedAt == null) {
    return channel.memberlessSince != null;
  }
  return channel.archivedReason === "dormant_memberless";
}

interface LifecycleChannelsSectionProps {
  channels: readonly OrgChannelSummary[];
  onInspectChannel: (channelId: string) => void;
  onAddActor: (channelId: string) => void;
}

/**
 * The lifecycle ledger: every channel that is no longer represented in the
 * actor fleet grid — memberless-but-unarchived, archived, or soft-deleted.
 * Every row stays inspectable ("View history"); eligible manual rooms also
 * offer a way back in.
 */
export const LifecycleChannelsSection: FC<LifecycleChannelsSectionProps> = ({
  channels,
  onInspectChannel,
  onAddActor,
}) => {
  const lifecycleChannels = selectLifecycleChannels(channels);
  if (lifecycleChannels.length === 0) {
    return null;
  }
  return (
    <section className="relative z-10 mb-3 border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900">
      <div className="mb-2 font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500">
        Inactive &amp; retained channels
      </div>
      <table className="w-full table-fixed border-collapse">
        <colgroup>
          <col />
          <col className="w-[130px]" />
          <col className="w-[178px]" />
          <col className="w-[128px]" />
          <col className="w-[196px]" />
        </colgroup>
        <thead>
          <tr className="border-b border-slate-200 dark:border-slate-800">
            {["channel", "provenance", "status", "meta"].map((heading) => (
              <th
                key={heading}
                className="px-2 pb-1.5 text-left font-mono text-[8px] font-bold uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500"
              >
                {heading}
              </th>
            ))}
            <th className="px-2 pb-1.5 text-right font-mono text-[8px] font-bold uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">
              action
            </th>
          </tr>
        </thead>
        <tbody>
          {lifecycleChannels.map((channel) => (
            <LifecycleChannelRow
              key={channel.id}
              channel={channel}
              onInspectChannel={onInspectChannel}
              onAddActor={onAddActor}
            />
          ))}
        </tbody>
      </table>
    </section>
  );
};

interface LifecycleChannelRowProps {
  channel: OrgChannelSummary;
  onInspectChannel: (id: string) => void;
  onAddActor: (id: string) => void;
}

function LifecycleChannelRow({
  channel,
  onInspectChannel,
  onAddActor,
}: LifecycleChannelRowProps) {
  const status = lifecycleStatus(channel);
  return (
    <tr
      key={channel.id}
      className="border-b border-slate-100 transition-colors last:border-0 hover:bg-slate-50 dark:border-slate-800/60 dark:hover:bg-slate-800/30"
    >
      <td className="px-2 py-2">
        <span className="flex items-center gap-1 truncate font-mono text-[11px] font-semibold text-slate-700 dark:text-slate-200">
          <span className="text-emerald-600 dark:text-emerald-400">#</span>
          <span className="truncate">{channel.displayName}</span>
        </span>
      </td>
      <td className="px-2 py-2">
        <span className="inline-flex items-center whitespace-nowrap border border-slate-300 px-1.5 py-0.5 font-mono text-[8px] uppercase tracking-[0.14em] text-slate-500 dark:border-slate-600 dark:text-slate-400">
          {provenanceLabel(channel)}
        </span>
      </td>
      <td className="px-2 py-2">
        <HudStatusPill tone={status.tone} label={status.label} />
      </td>
      <td className="whitespace-nowrap px-2 py-2 font-mono text-[9px] tabular-nums text-slate-400 dark:text-slate-500">
        {channel.deletedAt != null
          ? `${formatRelativeTime(channel.deletedAt)} · history kept`
          : `idle ${formatRelativeTime(channel.memberlessSince ?? channel.lastActivityAt)}`}
      </td>
      <td className="px-2 py-2">
        <div className="flex justify-end gap-1.5">
          <Button
            size="xs"
            variant="secondary"
            className="whitespace-nowrap"
            onClick={() => onInspectChannel(channel.id)}
          >
            View history
          </Button>
          {canAddActorToLifecycleChannel(channel) && (
            <Button
              size="xs"
              className="whitespace-nowrap"
              onClick={() => onAddActor(channel.id)}
            >
              Add actor
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}
